import { MyNaviTypography } from '@/components/Campaign/mynavi/MyNaviUI/MyNaviTypography'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { useRef, useState } from 'react'
import type { AccordionItem } from './MyNaviFilterAccordionList'
import { MyNaviFilterList } from './MyNaviFilterList'

export type JobCategoryLeafItem = AccordionItem

export type JobCategoryMiddleGroup = {
  id: number
  name: string
  items: JobCategoryLeafItem[]
  /** 「すべて」行のチェック状態 */
  allChecked: boolean
  /** 「すべて」行クリック時のコールバック */
  onToggleAll: () => void
  defaultExpanded?: boolean
}

export type JobCategoryTopGroup = {
  id: number
  name: string
  middleGroups: JobCategoryMiddleGroup[]
  defaultExpanded?: boolean
}

export type MyNaviFilterJobCategoryAccordionProps = {
  groups: JobCategoryTopGroup[]
}

const FONT_STYLE = { fontFamily: "'BIZ UDPGothic', sans-serif" }

export function MyNaviFilterJobCategoryAccordion({ groups }: MyNaviFilterJobCategoryAccordionProps) {
  const prevGroupsRef = useRef(groups)
  const [expandedTop, setExpandedTop] = useState<Set<number>>(
    () => new Set(groups.filter((g) => g.defaultExpanded).map((g) => g.id)),
  )
  const [expandedMiddle, setExpandedMiddle] = useState<Set<number>>(
    () => new Set(groups.flatMap((g) => g.middleGroups.filter((m) => m.defaultExpanded).map((m) => m.id))),
  )

  // groups が非同期で後から変化した場合（空→データあり）に defaultExpanded を反映する
  if (groups !== prevGroupsRef.current) {
    prevGroupsRef.current = groups
    setExpandedTop((prev) => {
      const next = new Set(prev)
      for (const group of groups) {
        if (group.defaultExpanded && !next.has(group.id)) {
          next.add(group.id)
        }
      }
      return next.size === prev.size ? prev : next
    })
    setExpandedMiddle((prev) => {
      const next = new Set(prev)
      for (const group of groups) {
        for (const middle of group.middleGroups) {
          if (middle.defaultExpanded && !next.has(middle.id)) {
            next.add(middle.id)
          }
        }
      }
      return next.size === prev.size ? prev : next
    })
  }

  const toggleTop = (id: number) => {
    setExpandedTop((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleMiddle = (id: number) => {
    setExpandedMiddle((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div className="flex flex-col">
      {groups.map((topGroup) => {
        const isTopExpanded = expandedTop.has(topGroup.id)
        return (
          <div key={topGroup.id}>
            {/* 大分類ヘッダー */}
            <button
              type="button"
              onClick={() => toggleTop(topGroup.id)}
              className="flex items-center justify-between w-full h-[50px] px-4 bg-white border-b border-[#e5e5e5]"
            >
              <span className="text-[#686868] text-[14px] font-bold" style={FONT_STYLE}>
                {topGroup.name}
              </span>
              {isTopExpanded ? (
                <IconChevronUp size={20} color="#686868" />
              ) : (
                <IconChevronDown size={20} color="#686868" />
              )}
            </button>

            {/* 大分類展開時: 中分類アコーディオン */}
            {isTopExpanded &&
              topGroup.middleGroups.map((middleGroup) => {
                const isMiddleExpanded = expandedMiddle.has(middleGroup.id)
                return (
                  <div key={middleGroup.id}>
                    {/* 中分類ヘッダー */}
                    <button
                      type="button"
                      onClick={() => toggleMiddle(middleGroup.id)}
                      className="flex items-center justify-between w-full h-[44px] pl-8 pr-4 bg-[#f0f0f0] border-b border-[#e5e5e5]"
                    >
                      <MyNaviTypography variant="label">{middleGroup.name}</MyNaviTypography>
                      {isMiddleExpanded ? (
                        <IconChevronUp size={16} color="#686868" />
                      ) : (
                        <IconChevronDown size={16} color="#686868" />
                      )}
                    </button>

                    {/* 中分類展開時: 「すべて」+ 小分類チェックボックス */}
                    {isMiddleExpanded && (
                      <MyNaviFilterList
                        items={middleGroup.items}
                        allChecked={middleGroup.allChecked}
                        onToggleAll={middleGroup.onToggleAll}
                      />
                    )}
                  </div>
                )
              })}
          </div>
        )
      })}
    </div>
  )
}
